import { useCallback } from 'react';
import { supabase } from '@/data/supabase/client';

const BUCKET = 'activity-images';
const MIN_DIMENSION = 500;
const MAX_DIMENSION = 1600;
const WEBP_QUALITY = 0.85;
const ALLOWED_MIME = ['image/jpeg', 'image/png', 'image/webp'] as const;

export type ImageUploadError =
  | 'INVALID_FORMAT'
  | 'TOO_SMALL'
  | 'TOO_LARGE'
  | 'UPLOAD_FAILED';

export class ActivityImageUploadError extends Error {
  constructor(public code: ImageUploadError, message: string) {
    super(message);
    this.name = 'ActivityImageUploadError';
  }
}

async function loadImage(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new ActivityImageUploadError('INVALID_FORMAT', 'Nepodařilo se načíst obrázek'));
    };
    img.src = url;
  });
}

async function resizeToWebP(img: HTMLImageElement): Promise<Blob> {
  const longEdge = Math.max(img.naturalWidth, img.naturalHeight);
  const scale = longEdge > MAX_DIMENSION ? MAX_DIMENSION / longEdge : 1;
  const width = Math.round(img.naturalWidth * scale);
  const height = Math.round(img.naturalHeight * scale);

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new ActivityImageUploadError('UPLOAD_FAILED', 'Canvas context unavailable');
  ctx.drawImage(img, 0, 0, width, height);

  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new ActivityImageUploadError('UPLOAD_FAILED', 'Komprese selhala'));
          return;
        }
        resolve(blob);
      },
      'image/webp',
      WEBP_QUALITY
    );
  });
}

function parseStoragePath(publicUrl: string): string | null {
  // URL format: {SUPABASE_URL}/storage/v1/object/public/activity-images/{business_id}/{uuid}.webp
  const marker = `/storage/v1/object/public/${BUCKET}/`;
  const idx = publicUrl.indexOf(marker);
  if (idx === -1) return null;
  return publicUrl.slice(idx + marker.length);
}

export function useActivityImageUpload() {
  const uploadImage = useCallback(async (file: File, businessId: number): Promise<string> => {
    if (!ALLOWED_MIME.includes(file.type as typeof ALLOWED_MIME[number])) {
      throw new ActivityImageUploadError('INVALID_FORMAT', 'Povolené formáty: JPEG, PNG, WebP');
    }

    const img = await loadImage(file);
    if (img.naturalWidth < MIN_DIMENSION || img.naturalHeight < MIN_DIMENSION) {
      throw new ActivityImageUploadError(
        'TOO_SMALL',
        `Obrázek musí mít minimálně ${MIN_DIMENSION}×${MIN_DIMENSION} px`
      );
    }

    const blob = await resizeToWebP(img);
    const uuid = crypto.randomUUID();
    const path = `${businessId}/${uuid}.webp`;

    const { error } = await supabase.storage.from(BUCKET).upload(path, blob, {
      contentType: 'image/webp',
      cacheControl: '31536000',
      upsert: false,
    });

    if (error) {
      if (error.message.toLowerCase().includes('exceed')) {
        throw new ActivityImageUploadError('TOO_LARGE', 'Soubor je příliš velký (max 5 MB)');
      }
      throw new ActivityImageUploadError('UPLOAD_FAILED', error.message);
    }

    const { data } = supabase.storage.from(BUCKET).getPublicUrl(path);
    return data.publicUrl;
  }, []);

  const deleteImage = useCallback(async (publicUrl: string): Promise<void> => {
    const path = parseStoragePath(publicUrl);
    if (!path) {
      console.error('deleteImage: could not parse path from URL', publicUrl);
      return;
    }
    const { error } = await supabase.storage.from(BUCKET).remove([path]);
    if (error) {
      console.error('deleteImage: storage remove failed', error);
    }
  }, []);

  return { uploadImage, deleteImage };
}
